import { useCallback, useMemo, useState } from "react";

import * as ModelConverter from "@/components/features/_model/converter";

import * as Factory from "./factory";
import * as FormModelType from "./type";
import * as ValidationSchema from "./validation-schema";

export const useReOnboardingRequestFormModel = (
	initInputModel: FormModelType.ReOnboardingRequestFormModel["InputModel"],
) => {
	const defaultInputModel = useMemo(
		() => Factory.Default.reOnboardingRequestInputModelFactory(initInputModel.hotelID, initInputModel.updatedEmail),
		[initInputModel.hotelID, initInputModel.updatedEmail],
	);

	// InputModel の初期化
	const [currentInputModel, setCurrentInputModel] =
		useState<FormModelType.ReOnboardingRequestFormModel["InputModel"]>(initInputModel);

	// ViewModel の初期化
	const [viewModel, setViewModel] = useState<FormModelType.ReOnboardingRequestFormModel["ViewModel"]>(
		ModelConverter.inputModelToViewModelConverter<
			FormModelType.ReOnboardingRequestFormModel["InputModel"],
			FormModelType.ExcludeKeys,
			FormModelType.ReOnboardingRequestFormModel["QueryData"],
			ValidationSchema.ReOnboardingRequestInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: initInputModel,
			currentInput: initInputModel,
			excludeKeys: FormModelType.excludeKeys,
			zodSchema: ValidationSchema.ReOnboardingRequestInputZodSchema,
		}),
	);

	// ViewModel の更新
	const onSetViewModel = useCallback(
		(_viewModel: FormModelType.ReOnboardingRequestFormModel["ViewModel"]) => {
			const convertedInputModel = ModelConverter.viewModelToInputModelConverter(_viewModel);
			const convertedViewModel = ModelConverter.inputModelToViewModelConverter<
				FormModelType.ReOnboardingRequestFormModel["InputModel"],
				FormModelType.ExcludeKeys,
				FormModelType.ReOnboardingRequestFormModel["QueryData"],
				ValidationSchema.ReOnboardingRequestInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: initInputModel,
				currentInput: convertedInputModel,
				excludeKeys: FormModelType.excludeKeys,
				zodSchema: ValidationSchema.ReOnboardingRequestInputZodSchema,
			});
			// state の更新
			setCurrentInputModel(convertedInputModel);
			setViewModel(convertedViewModel);
		},
		[defaultInputModel, initInputModel],
	);

	const onResetViewModel = useCallback(() => {
		setViewModel(
			ModelConverter.inputModelToViewModelConverter<
				FormModelType.ReOnboardingRequestFormModel["InputModel"],
				FormModelType.ExcludeKeys,
				FormModelType.ReOnboardingRequestFormModel["QueryData"],
				ValidationSchema.ReOnboardingRequestInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: initInputModel,
				currentInput: initInputModel,
				excludeKeys: FormModelType.excludeKeys,
				zodSchema: ValidationSchema.ReOnboardingRequestInputZodSchema,
			}),
		);
	}, [defaultInputModel, initInputModel]);

	return {
		inputModel: currentInputModel,
		viewModel,
		onSetViewModel,
		onResetViewModel,
	};
};
